import type { ErrorCode, Problem } from '@autopilot/contracts';

/** Fehler aus der API als RFC-9457-Problem (oder Netzwerkfehler mit code NETWORK). */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode | 'NETWORK' | 'UNKNOWN';
  readonly problem: Partial<Problem> | null;
  readonly fieldErrors: Record<string, string>;
  readonly correlationId: string | null;

  constructor(status: number, problem: Partial<Problem> | null, fallbackCode: 'NETWORK' | 'UNKNOWN' = 'UNKNOWN') {
    super(problem?.title ?? `HTTP ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.code = (problem?.code as ErrorCode | undefined) ?? codeFromStatus(status) ?? fallbackCode;
    this.fieldErrors = problem?.fieldErrors ?? {};
    this.correlationId = problem?.correlationId ?? null;
  }
}

function codeFromStatus(status: number): ErrorCode | null {
  switch (status) {
    case 401:
      return 'UNAUTHENTICATED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 409:
      return 'VERSION_CONFLICT';
    case 422:
      return 'VALIDATION_FAILED';
    case 429:
      return 'RATE_LIMITED';
    case 502:
    case 503:
    case 504:
      return 'DEPENDENCY_UNAVAILABLE';
    default:
      return status >= 500 ? 'INTERNAL' : null;
  }
}

/** Verständliche deutsche Texte je Code, ohne interne Details (06: „keine internen Tabellen-/Providerfehler“). */
export const ERROR_TEXT: Record<string, { message: string; next: string }> = {
  AUTOMATION_DISABLED: { message: 'Automatische Läufe sind für diese Gesellschaft ausgeschaltet.', next: 'Aktivieren Sie die Gesellschaft in der Administration oder starten Sie einen manuellen Batch.' },
  POLICY_CHANGED: { message: 'Die Gesellschaftsregel wurde inzwischen geändert.', next: 'Laden Sie die Seite neu; der nächste Lauf nutzt die neue Regel.' },
  POLICY_NOT_SYNCED: { message: 'Die Gesellschaftsregel ist in Nidus noch nicht bestätigt.', next: 'Warten Sie die Übertragung ab und prüfen Sie den Status in einigen Sekunden erneut.' },
  TICKET_TOO_YOUNG: { message: 'Das Ticket ist jünger als 30 Minuten.', next: 'Es wird frühestens im nächsten Lauf berücksichtigt.' },
  MANUAL_CLAIM_ACTIVE: { message: 'Das Ticket wird gerade von einer anderen Person geprüft.', next: 'Warten Sie, bis die Bearbeitungssperre abläuft, oder wählen Sie ein anderes Ticket.' },
  AUTOMATION_HOLD: { message: 'Das Ticket ist für die Automatik zurückgestellt.', next: 'Heben Sie die Zurückstellung auf, wenn es wieder automatisch bearbeitet werden soll.' },
  EXECUTION_DISABLED: { message: 'Der globale Not-Stopp ist aktiv. Es werden keine Antworten versendet.', next: 'Ein Admin kann die Ausführung unter Administration → Betrieb wieder freigeben.' },
  INSTANCE_MISMATCH: { message: 'Die Nidus-Installation passt nicht zu dieser Autopilot-Installation.', next: 'Wenden Sie sich an den Betrieb; es werden keine Daten verarbeitet.' },
  SELECTION_EXPIRED: { message: 'Die Ticketauswahl ist abgelaufen.', next: 'Erstellen Sie mit denselben Filtern eine neue Auswahl.' },
  TICKET_CHANGED: { message: 'Das Ticket wurde in Nidus seit der Auswahl geändert.', next: 'Erstellen Sie einen neuen Batch mit aktuellem Kontext.' },
  SOURCE_CHANGED: { message: 'Eine Quelle des Tickets hat sich geändert.', next: 'Eine neue Prüfung mit aktuellem Kontext ist erforderlich.' },
  FOCUS_RESERVED: { message: 'Das Ticket ist in Nidus gerade im Fokus reserviert.', next: 'Es wird nicht übernommen; versuchen Sie es später erneut.' },
  ALREADY_RESOLVED: { message: 'Das Ticket ist bereits gelöst.', next: 'Keine weitere Aktion nötig.' },
  OPERATION_ACTIVE: { message: 'Für dieses Ticket läuft bereits eine Ausführung.', next: 'Warten Sie deren Ergebnis ab.' },
  PROFILE_NOT_ALLOWED: { message: 'Für diese Gesellschaft ist kein erlaubtes Bearbeiterprofil vorhanden.', next: 'Legen Sie unter Administration → Bearbeiter ein aktives Profil für die Gesellschaft an.' },
  EXCLUSION_MATCH: { message: 'Das Ticket enthält einen Begriff der Negativliste.', next: 'Es bleibt von der automatischen Bearbeitung ausgeschlossen.' },
  CONTEXT_INCOMPLETE: { message: 'Der Ticketkontext ist unvollständig.', next: 'Bearbeiten Sie das Ticket manuell in Nidus.' },
  CONTEXT_TOO_LARGE: { message: 'Der notwendige Kontext ist zu groß für das Modell.', next: 'Bearbeiten Sie das Ticket manuell in Nidus.' },
  NO_RECIPIENT: { message: 'Es gibt keinen erreichbaren Empfänger.', next: 'Ergänzen Sie in Nidus eine Empfängeradresse.' },
  DELIVERY_SUPPRESSED: { message: 'Der Versand ist für diese Gesellschaft gesperrt.', next: 'Prüfen Sie die Versandfreigabe in Nidus.' },
  DELIVERY_FAILED: { message: 'Der Versand ist fehlgeschlagen.', next: 'Prüfen Sie das Ticket in Nidus.' },
  DELIVERY_UNKNOWN: { message: 'Ob die Antwort zugestellt wurde, ist ungeklärt.', next: 'Klären Sie den Versandstatus in Nidus; es wird nichts automatisch wiederholt.' },
  SENT_NOT_RESOLVED: { message: 'Die Antwort wurde versendet, das Ticket konnte aber nicht gelöst werden.', next: 'Prüfen Sie das Ticket in Nidus.' },
  IDEMPOTENCY_MISMATCH: { message: 'Die Aktion wurde bereits mit anderen Daten ausgelöst.', next: 'Laden Sie die Seite neu und prüfen Sie den aktuellen Stand.' },
  RATE_LIMITED: { message: 'Zu viele Anfragen in kurzer Zeit.', next: 'Warten Sie einen Moment und versuchen Sie es erneut.' },
  VERSION_CONFLICT: { message: 'Die Daten wurden zwischenzeitlich geändert.', next: 'Die Ansicht wurde neu geladen – prüfen Sie den aktuellen Stand und wiederholen Sie die Aktion.' },
  VALIDATION_FAILED: { message: 'Einige Angaben sind ungültig oder unvollständig.', next: 'Korrigieren Sie die markierten Felder.' },
  NOT_FOUND: { message: 'Der Eintrag wurde nicht gefunden oder ist nicht mehr verfügbar.', next: 'Kehren Sie zur Übersicht zurück.' },
  UNAUTHENTICATED: { message: 'Ihre Anmeldung ist abgelaufen.', next: 'Bitte melden Sie sich erneut an.' },
  FORBIDDEN: { message: 'Sie haben für diese Aktion keine Berechtigung.', next: 'Wenden Sie sich an einen Autopilot-Admin, wenn Sie Zugriff benötigen.' },
  DEPENDENCY_UNAVAILABLE: { message: 'Ein angebundenes System (z. B. Nidus) ist gerade nicht erreichbar.', next: 'Versuchen Sie es in einigen Minuten erneut.' },
  INTERNAL: { message: 'Auf dem Server ist ein unerwarteter Fehler aufgetreten.', next: 'Versuchen Sie es erneut; bleibt der Fehler, wenden Sie sich an den Betrieb.' },
  NETWORK: { message: 'Der Autopilot-Server ist nicht erreichbar.', next: 'Prüfen Sie Ihre Verbindung und versuchen Sie es erneut.' },
  UNKNOWN: { message: 'Die Aktion konnte nicht ausgeführt werden.', next: 'Versuchen Sie es erneut.' },
};

export function describeError(err: unknown, action?: string): { title: string; message: string; next: string; code: string; correlationId: string | null } {
  const e = err instanceof ApiError ? err : null;
  const code = e?.code ?? 'UNKNOWN';
  const text = ERROR_TEXT[code] ?? ERROR_TEXT.UNKNOWN!;
  return {
    title: action ? `${action} fehlgeschlagen` : 'Fehler',
    message: text.message,
    next: text.next,
    code,
    correlationId: e?.correlationId ?? null,
  };
}

export function isForbidden(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 403 || err.code === 'FORBIDDEN');
}
export function isConflict(err: unknown): boolean {
  return err instanceof ApiError && err.status === 409;
}
